"use client";

import { QueryClientProvider } from "@tanstack/react-query";
import dynamic from "next/dynamic";
import { usePathname, useSearchParams } from "next/navigation";
import { Suspense, useEffect, useRef, useState } from "react";
import { CartPurchasabilitySync } from "@/features/cart/ui/components/CartPurchasabilitySync";
import { CompareFloatingBar } from "@/features/products/ui/components/CompareFloatingBar";
import { VirtualTryOnJobsProvider } from "@/features/virtual-try-on/ui";
import {
  captureSourceAttribution,
  identifyUser,
  resetUser,
  setAmplitudeConsent,
  setGtmConsent,
  setUserProperties,
  trackPageView,
} from "@/lib/client/analytics";
import { useSession } from "@/lib/client/auth/client";
import { useSyncUser } from "@/lib/client/auth/syncUser";
import {
  ConsentProvider,
  CookieBanner,
  CookiePreferences,
  useConsent,
  useConsentCategory,
} from "@/lib/client/consent";
import { queryClient } from "@/lib/client/queryClient";
import { useLoadingStore } from "@/lib/client/stores";
import { isProductionTelemetryEnabled } from "@/lib/client/telemetry";
import { LoadingOverlay } from "@/ui";

// Lazy load SupportChatWidget to reduce initial bundle size
const SupportChatWidget = dynamic(
  () => import("@/features/support-chat/ui").then((mod) => mod.SupportChatWidget),
  { ssr: false, loading: () => null },
);

function DeferredSupportChatWidget() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const timerId = setTimeout(() => setReady(true), 4_000);
    return () => clearTimeout(timerId);
  }, []);

  if (!ready) {
    return null;
  }

  return <SupportChatWidget />;
}

/**
 * Defer work using requestIdleCallback or setTimeout fallback
 * to avoid blocking the main thread during initial page load
 */
function defer(callback: () => void, delay = 2000): void {
  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    window.requestIdleCallback(() => setTimeout(callback, delay), { timeout: 5000 });
  } else {
    setTimeout(callback, delay);
  }
}

/**
 * GTMInitializer - Gated behind analytics consent
 * Google Ads remains denied unless the visitor separately grants marketing consent.
 * Deferred to avoid blocking initial page render
 */
function GTMInitializer() {
  const analyticsConsented = useConsentCategory("analytics");
  const marketingConsented = useConsentCategory("marketing");
  const [gtmLoaded, setGtmLoaded] = useState(false);

  useEffect(() => {
    if (!(isProductionTelemetryEnabled() && analyticsConsented) || gtmLoaded) return;

    const gtmId = process.env.NEXT_PUBLIC_GTM_ID;
    if (!gtmId) {
      if (process.env.NODE_ENV === "development") {
        console.warn("NEXT_PUBLIC_GTM_ID is not set; GTM is disabled.");
      }
      return;
    }

    defer(() => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        "gtm.start": new Date().getTime(),
        event: "gtm.js",
      });
      setGtmConsent("default", analyticsConsented, marketingConsented);

      const existing = document.querySelector('script[src*="googletagmanager.com/gtm.js"]');
      if (!existing) {
        const script = document.createElement("script");
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
        document.head.appendChild(script);
      }

      setGtmLoaded(true);
    }, 1500);
  }, [analyticsConsented, marketingConsented, gtmLoaded]);

  useEffect(() => {
    if (!(isProductionTelemetryEnabled() && gtmLoaded)) return;

    setGtmConsent("update", analyticsConsented, marketingConsented);
  }, [analyticsConsented, marketingConsented, gtmLoaded]);

  return null;
}

function AmplitudeInitializer() {
  const analyticsConsented = useConsentCategory("analytics");
  const { isLoaded } = useConsent();
  const { data: session, isPending } = useSession();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const initialPageViewTrackedRef = useRef(false);

  useEffect(() => {
    if (!isLoaded) return;

    setAmplitudeConsent(analyticsConsented);
  }, [analyticsConsented, isLoaded]);

  useEffect(() => {
    if (!(analyticsConsented && isLoaded)) return;

    captureSourceAttribution();
    setUserProperties({});
  }, [analyticsConsented, isLoaded]);

  useEffect(() => {
    if (!analyticsConsented || isPending) return;

    const sessionUserId = session?.user?.id || session?.user?.email;

    if (!sessionUserId) {
      resetUser();
      return;
    }

    identifyUser(sessionUserId, {
      email: session?.user?.email,
      email_domain: session?.user?.email?.split("@")[1],
    });
  }, [analyticsConsented, isPending, session]);

  useEffect(() => {
    if (!(analyticsConsented && isLoaded) || initialPageViewTrackedRef.current) {
      return;
    }

    const query = searchParams.toString();
    const pagePath = query ? `${pathname}?${query}` : pathname;
    captureSourceAttribution();
    trackPageView(pagePath, document.title);
    initialPageViewTrackedRef.current = true;
  }, [analyticsConsented, isLoaded, pathname, searchParams]);

  return null;
}

/**
 * GTM NoScript component for noscript fallback
 * This must be rendered in the body
 */
function GTMNoScript() {
  const marketingConsented = useConsentCategory("marketing");

  if (!(isProductionTelemetryEnabled() && marketingConsented)) return null;

  const gtmId = process.env.NEXT_PUBLIC_GTM_ID;
  if (!gtmId) return null;

  return (
    <noscript>
      <iframe
        src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
        height="0"
        width="0"
        style={{ display: "none", visibility: "hidden" }}
        title="Google Tag Manager"
      />
    </noscript>
  );
}

function RouteAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const hasMountedRef = useRef(false);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    const query = searchParams.toString();
    const pagePath = query ? `${pathname}?${query}` : pathname;
    trackPageView(pagePath, document.title);
  }, [pathname, searchParams]);

  return null;
}

function AuthSync() {
  useSyncUser();
  return null;
}

function GlobalLoadingOverlay() {
  const isLoading = useLoadingStore((state) => state.isLoading);
  return <LoadingOverlay isLoading={isLoading} />;
}

function ConsentAwareComponents() {
  const [prefsOpen, setPrefsOpen] = useState(false);

  return (
    <>
      <CookieBanner
        open={!prefsOpen}
        onCustomize={() => setPrefsOpen(true)}
      />
      <CookiePreferences
        open={prefsOpen}
        onOpenChange={setPrefsOpen}
      />
      <GTMNoScript />
    </>
  );
}

function InnerProviders({ children }: { children: React.ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={null}>
        <AuthSync />
        <CartPurchasabilitySync />
        <RouteAnalytics />
      </Suspense>
      <Suspense fallback={null}>
        <AmplitudeInitializer />
      </Suspense>
      <GTMInitializer />
      <GlobalLoadingOverlay />
      <VirtualTryOnJobsProvider>
        {children}
        <CompareFloatingBar />
        <Suspense fallback={null}>
          <DeferredSupportChatWidget />
        </Suspense>
      </VirtualTryOnJobsProvider>
    </QueryClientProvider>
  );
}

export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <ConsentProvider>
      <InnerProviders>
        {children}
        <ConsentAwareComponents />
      </InnerProviders>
    </ConsentProvider>
  );
}
